/**
 * Content-aware width for the Table column, so its slack goes to Column — the
 * flexible one, holding the longest labels. Derived from the option list rather than
 * the selected value, so every row of a group stays aligned without coordination.
 */

/** td padding (16) + leading icon (16) + gap (8) + the caret zone (24). */
export const OPTION_CELL_CHROME: number = 64;

export const TABLE_COLUMN_MIN_WIDTH: number = 80;

/** The old equal-split width: capping here means we can only match or beat it. */
export const TABLE_COLUMN_MAX_WIDTH: number = 150;

/** Canvas runs a fraction under layout, and an exact fit still ellipsises. */
const LABEL_SAFETY_MARGIN: number = 2;

/** Matches `.kuika-typography-text` and the app font. */
const LABEL_FONT: string = "400 14px Inter, sans-serif";

let labelMeasureContext: CanvasRenderingContext2D | null | undefined;

/** Canvas keeps this off the layout path — no reflow. */
export const measureLabelWidths = (labels: string[]): number[] => {
  if (labelMeasureContext === undefined) {
    labelMeasureContext = document.createElement("canvas").getContext("2d");
    if (labelMeasureContext) {
      labelMeasureContext.font = LABEL_FONT;
    }
  }
  const context: CanvasRenderingContext2D | null = labelMeasureContext;
  if (!context) {
    return [];
  }
  return labels.map((label: string) => context.measureText(label).width);
};

/** Empty list = not measurable, so keep the previous fixed width. */
export const resolveTableColumnWidth = (labelWidths: number[]): number => {
  if (labelWidths.length === 0) {
    return TABLE_COLUMN_MAX_WIDTH;
  }
  const widest: number = labelWidths.reduce((max: number, width: number) => (width > max ? width : max), 0);
  const needed: number = Math.ceil(widest) + LABEL_SAFETY_MARGIN + OPTION_CELL_CHROME;
  return Math.min(TABLE_COLUMN_MAX_WIDTH, Math.max(TABLE_COLUMN_MIN_WIDTH, needed));
};

export const getTableColumnWidth = (labels: string[]): number => {
  return resolveTableColumnWidth(measureLabelWidths(labels));
};
